import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import { createFormData } from "../../../utils/form-data/create-form-data.ts"
import type { SubcontractorsFilterOptions } from "../redux/subcontractors.types.ts"
import type {
  SubcontractorComplianceDocument,
  SubcontractorListItem,
  SubcontractorProjectRow
} from "../types/types.ts"
import type {
  CommentSubcontractorParams,
  CreateInviteSubcontractorData,
  CreateSubcontractorData,
  DelayedSubcontractorResponse,
  GetSubcontractorsParams,
  RateSubcontractorData,
  RegisterSubcontractorParams,
  SubcontractorId,
  SubcontractorIdData,
  UpdateSubcontractorData,
  UploadComplianceDocumentData
} from "./services.types.ts"

const DELAY = 500
const BASE_URL = "/subcontractor/subcontractors"
const BASE_URL_DETAILS = (id: SubcontractorId) =>
  `${BASE_URL}/${id && `${id}/`}`

const PATHS = {
  getSubcontractors: `${BASE_URL}/`,
  getSubcontractorsFilterOptions: `${BASE_URL}/filter_options/`,
  createInviteSubcontractor: `${BASE_URL}/invite/`,
  createSubcontractor: `${BASE_URL}/create_subcontractor/`,
  getSubcontractor: BASE_URL_DETAILS,
  updateSubcontractor: BASE_URL_DETAILS,
  deleteSubcontractor: BASE_URL_DETAILS,
  //
  getSubcontractorProjects: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}projects/`,
  suspendSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}block/`,
  activateSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}unblock/`,
  resendInviteSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}resend_invite/`,
  inviteNoAccess: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}invite_no_access/`,
  commentSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}comment/`,
  rateSubcontractor: (id: SubcontractorId) => `${BASE_URL_DETAILS(id)}rate/`,
  uploadComplianceDocument: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}upload_compliance_document/`,
  registerSubcontractor: (id: SubcontractorId) =>
    `${BASE_URL_DETAILS(id)}register/`
}

export const getSubcontractorsFilterOptions = () => {
  return request.api.get<SubcontractorsFilterOptions, BaseApiResponse<unknown>>(
    PATHS.getSubcontractorsFilterOptions
  )
}

export const getSubcontractors = (params?: GetSubcontractorsParams) => {
  return request.api.get<
    BasePagination<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.getSubcontractors, params)
}

/** Sidebar badge: how many subcontractors have a supplied document that is
 * expiring soon or expired. Live-derived on the server, never a stored count. */
export const getSubcontractorComplianceAlertCount = () => {
  return request.api.get<{ count: number }, BaseApiResponse<unknown>>(
    `${BASE_URL}/compliance_alert_count/`
  )
}

export const getSubcontractorDetails = (id: SubcontractorId) => {
  return request.api.get<SubcontractorListItem, BaseApiResponse<unknown>>(
    PATHS.getSubcontractor(id)
  )
}

/**
 * The projects a subcontractor is engaged on, a row each.
 *
 * Its own endpoint rather than a field on the detail payload: the profile pages
 * it and filters it by status, and the server narrows the rows to the projects
 * the caller may actually see — an assignment is not a way to read a project
 * they have no access to.
 */
export const getSubcontractorProjects = (
  id: SubcontractorId,
  params?: { project_status?: number; page?: number; page_size?: number }
) => {
  return request.api.get<
    BasePagination<SubcontractorProjectRow>,
    BaseApiResponse<unknown>
  >(PATHS.getSubcontractorProjects(id), params)
}

export const createInviteSubcontractor = (
  data: CreateInviteSubcontractorData
) => {
  // An invitation can now carry the same optional profile as a direct add,
  // including document uploads, which need multipart — a plain invite stays
  // JSON, unchanged from before.
  const hasFile = Object.values(data).some((value) => value instanceof File)

  if (hasFile) {
    return request.api.post<
      BaseApiResponse<SubcontractorListItem>,
      BaseApiResponse<unknown>
    >(PATHS.createInviteSubcontractor, createFormData(data), {
      headers: { "Content-Type": "multipart/form-data" }
    })
  }

  return request.api.post<
    BaseApiResponse<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.createInviteSubcontractor, data)
}

export const updateSubcontractor = ({
  id,
  ...newInfo
}: UpdateSubcontractorData) => {
  // Only a re-uploaded W-9 (Stage 3) needs multipart here — everything else
  // a plain edit sends stays JSON, unchanged from before.
  const hasFile = Object.values(newInfo).some((value) => value instanceof File)

  if (hasFile) {
    // createFormData skips null, which is how a cleared field travels as
    // JSON; multipart has no null, so an explicit clear has to become ""
    // for DRF to read it back the same way. Untouched fields (undefined —
    // e.g. a secret like ach_account_number, which the shaping layer
    // deliberately leaves out rather than clearing) must stay dropped
    // instead of joining the same "" — coercing both to "" together is
    // what let re-uploading the W-9 silently wipe every other untouched
    // field, secrets included, in the same request.
    const clearedAsEmpty = Object.fromEntries(
      Object.entries(newInfo)
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, value === null ? "" : value])
    )
    return request.api.patch<
      BaseApiResponse<SubcontractorListItem>,
      BaseApiResponse<unknown>
    >(PATHS.updateSubcontractor(id), createFormData(clearedAsEmpty), {
      headers: { "Content-Type": "multipart/form-data" }
    })
  }

  return request.api.patch<
    BaseApiResponse<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.updateSubcontractor(id), newInfo)
}

/** Upload a missing compliance document, or replace one that already exists
 * — `document_type` decides which row the backend replaces in place
 * (`update_or_create`), so this is also how an expired document is renewed. */
export const uploadSubcontractorComplianceDocument = ({
  id,
  ...data
}: UploadComplianceDocumentData) => {
  return request.api.post<
    BaseApiResponse<SubcontractorComplianceDocument>,
    BaseApiResponse<unknown>
  >(PATHS.uploadComplianceDocument(id), createFormData(data), {
    headers: { "Content-Type": "multipart/form-data" }
  })
}

export const deleteSubcontractor = (id: SubcontractorId) => {
  return request.api.delete<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.deleteSubcontractor(id)
  )
}

export const suspendSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.suspendSubcontractor(id)
  )
}

export const activateSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.activateSubcontractor(id)
  )
}

export const resendInviteSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.resendInviteSubcontractor(id)
  )
}

export const inviteNoAccessSubcontractor = ({ id }: SubcontractorIdData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.inviteNoAccess(id)
  )
}

export const rateSubcontractor = ({ id, ...rest }: RateSubcontractorData) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.rateSubcontractor(id),
    rest
  )
}

export const createSubcontractor = (data: CreateSubcontractorData) => {
  // Carries the mandatory compliance-document files (Stage 1) and the W-9
  // (Stage 3), so this posts multipart rather than JSON.
  return request.api.post<
    BaseApiResponse<SubcontractorListItem>,
    BaseApiResponse<unknown>
  >(PATHS.createSubcontractor, createFormData(data), {
    headers: { "Content-Type": "multipart/form-data" }
  })
}

export const registerSubcontractor = (params: RegisterSubcontractorParams) => {
  return new Promise<DelayedSubcontractorResponse>((resolve) => {
    setTimeout(() => {
      resolve({ ok: true, data: {} } as DelayedSubcontractorResponse)
    }, DELAY)
  })
}

export const commentSubcontractor = (params: CommentSubcontractorParams) => {
  return new Promise<DelayedSubcontractorResponse>((resolve) => {
    setTimeout(() => {
      resolve({ ok: true, data: {} } as DelayedSubcontractorResponse)
    }, DELAY)
  })
}
